/**
 * Flavor Wheel Web Component
 *
 * A custom web component that renders an interactive flavor wheel using HTML5 Canvas.
 *
 * Usage:
 * <flavor-wheel
 *   data-labels='["Label 1", "Label 2", ...]'
 *   data-flavor-data='[2, 0, 1, 3, ...]'>
 * </flavor-wheel>
 *
 * Data format:
 * - data-labels: Array of strings for sector labels. Parts separated by " / " are
 *   treated as independent words and may be placed on separate concentric lines.
 * - data-flavor-data: Array of numbers (0-3) representing ring fill levels:
 *   0 = Empty [0,0,0]
 *   1 = Inner ring only [1,0,0]
 *   2 = Inner + middle rings [1,1,0]
 *   3 = All three rings [1,1,1]
 *
 * Features:
 * - Responsive design (canvas follows the container width, clamped per breakpoint)
 * - HiDPI/Retina display support
 * - Automatic sector count based on labels
 * - Curved text rendering around the wheel
 * - Collision-free labels (TEA-3xx): every line is measured against the arc length
 *   available in its sector, the label font is scaled down uniformly until all lines
 *   fit, and the outer padding grows with the number of label lines so nothing is
 *   clipped at the canvas edge. Re-renders once web fonts are loaded so measurements
 *   use the real Montserrat metrics.
 */

if (!customElements.get('flavor-wheel')) {
  class FlavorWheel extends HTMLElement {
    constructor() {
      super();
      this.canvas = null;
      this.context = null;
      this.canvasSize = 0;
      this.config = {
        startDegrees: -90,
        ringWidth: 36,
        ringGap: 0,
        innerPadding: 0,
        padding: 25,           // minimum outer padding; grows automatically with label lines
        lineCap: 'butt',
        fillColor: '#970514',
        gridColor: '#000000',
        gridRadialWidth: 1,
        gridCircleWidth: 1,
        labelColor: '#000000',
        labelFontFamily: 'Montserrat, sans-serif',
        labelFontWeight: '600',
        labelFontSize: 8,      // base size for the current breakpoint (set in computeSizing)
        labelFontMin: 6.5,     // never shrink below this (set in computeSizing)
        labelOffset: 8,        // gap between outer circle and first label line
        labelLineHeight: 1.3,  // line gap as a multiple of the font size
        labelArcFill: 0.88,    // share of the sector arc a line may occupy
        labelMaxLines: 3,
      };
    }

    connectedCallback() {
      this.initCanvas();
      this.parseDataAttributes();
      this.render();

      // Canvas text is measured with whatever font is available at render time. If the
      // web font arrives later, metrics change and labels could collide again, so
      // re-render once fonts are ready.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.render());
      }
    }

    disconnectedCallback() {
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    }

    static get observedAttributes() {
      return ['data-labels', 'data-flavor-data', 'data-n-sectors'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue && this.canvas) {
        this.parseDataAttributes();
        this.render();
      }
    }

    /**
     * Picks canvas size and label font sizes for the current viewport.
     * Below 1520px the canvas follows the container width (clamped 300–360px) so the
     * wheel uses the full mobile width instead of a fixed 300px square.
     */
    computeSizing() {
      const viewport = window.innerWidth;
      let size = 300;
      let fontSize = 8;
      let fontMin = 6.5;

      if (viewport > 1920) {
        size = 500;
        fontSize = 16;
        fontMin = 12;
      } else if (viewport > 1520) {
        size = 400;
        fontSize = 12;
        fontMin = 9;
      } else {
        const parentWidth = this.parentElement ? this.parentElement.clientWidth : 0;
        if (parentWidth > 0) {
          size = Math.round(Math.min(Math.max(parentWidth, 300), 360));
        }
        fontSize = size >= 340 ? 9 : 8;
        fontMin = 6.5;
      }

      this.config.labelFontSize = fontSize;
      this.config.labelFontMin = fontMin;
      return size;
    }

    applyCanvasSize(size) {
      const devicePixelRatio = window.devicePixelRatio || 1;
      this.canvasSize = size;
      this.canvas.width = size * devicePixelRatio;
      this.canvas.height = size * devicePixelRatio;
      // Intrinsic CSS size; if the container is narrower the canvas shrinks
      // proportionally instead of being squashed into an oval.
      this.canvas.style.width = size + 'px';
      this.canvas.style.height = 'auto';
      this.canvas.style.maxWidth = '100%';
      this.canvas.style.aspectRatio = '1 / 1';
      // Resizing a canvas resets its context state, so re-apply the DPR scale.
      this.context.setTransform(1, 0, 0, 1, 0, 0);
      this.context.scale(devicePixelRatio, devicePixelRatio);
    }

    initCanvas() {
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'flavor-wheel';
      this.context = this.canvas.getContext('2d');
      this.appendChild(this.canvas);
      this.applyCanvasSize(this.computeSizing());

      let resizeFrame = null;
      this.handleResize = () => {
        if (resizeFrame) cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => {
          resizeFrame = null;
          const newSize = this.computeSizing();
          if (newSize !== this.canvasSize) {
            this.applyCanvasSize(newSize);
          }
          this.render();
        });
      };

      window.addEventListener('resize', this.handleResize);
    }

    parseDataAttributes() {
      const labelsAttr = this.getAttribute('data-labels');

      if (labelsAttr) {
        this.config.labels = JSON.parse(labelsAttr);
      }

      const dataAttr = this.getAttribute('data-flavor-data');

      if (dataAttr) {
        const rawData = JSON.parse(dataAttr);
        this.config.data = this.convertNumbersToArrays(rawData);
      }

      this.config.numberOfSectors = this.config.labels ? this.config.labels.length : 8;
    }

    /**
     * Converts numeric values to ring configuration arrays
     * @param {(number|string)[]} data - Array of numbers (0-3)
     * @returns {number[][]} Array of [inner, middle, outer] configurations
     */
    convertNumbersToArrays(data) {
      return data.map((num) => {
        switch (String(num)) {
          case '1': return [1, 0, 0];
          case '2': return [1, 1, 0];
          case '3': return [1, 1, 1];
          default: return [0, 0, 0];
        }
      });
    }

    toRadians(degrees) {
      return degrees * Math.PI / 180;
    }

    labelFont(size) {
      return `${this.config.labelFontWeight} ${size}px ${this.config.labelFontFamily}`;
    }

    strokeArc(radius, startAngleDegrees, endAngleDegrees, color, width, lineCap = this.config.lineCap) {
      const centerX = this.canvasSize / 2, centerY = this.canvasSize / 2;

      this.context.beginPath();
      this.context.strokeStyle = color;
      this.context.lineWidth = width;
      this.context.lineCap = lineCap;
      this.context.arc(centerX, centerY, radius, this.toRadians(startAngleDegrees), this.toRadians(endAngleDegrees), false);
      this.context.stroke();
    }

    /**
     * Labels in the lower half are drawn flipped (glyph tops pointing to the centre).
     * Kept in one place so layout and drawing agree.
     */
    isFlipped(centerDegrees) {
      return Math.sin(this.toRadians(centerDegrees)) > -1e-6;
    }

    /**
     * Radius of line `index` out of `count` lines. Lines are stacked so that a reader
     * sees them in label order from top to bottom: for flipped (lower-half) labels the
     * glyph tops point inward, so the first line sits nearest the ring; for upper-half
     * labels the glyph tops point outward, so the first line sits outermost.
     */
    lineRadius(baseRadius, lineGap, index, count, flipped) {
      const slot = flipped ? index : count - 1 - index;
      return baseRadius + slot * lineGap;
    }

    /**
     * Splits one label into concentric lines for a given font size.
     * Parts separated by " / " are packed greedily (measured conservatively at the
     * innermost radius), then every line is verified against the arc available at the
     * radius it will actually be drawn on.
     * @returns {{lines: string[], fits: boolean}}
     */
    layoutLabel(label, fontSize, baseRadius, lineGap, sectorAngle, flipped) {
      this.context.font = this.labelFont(fontSize);
      const arcFill = this.config.labelArcFill;
      const fits = (text, radius) => this.context.measureText(text).width <= radius * sectorAngle * arcFill;

      if (fits(label, baseRadius)) {
        return { lines: [label], fits: true };
      }

      const parts = label.split(/\s*\/\s*/).map(part => part.trim()).filter(Boolean);
      const lines = [];
      let current = '';

      for (const part of parts) {
        const candidate = current ? `${current} / ${part}` : part;

        if (!current || fits(candidate, baseRadius)) {
          current = candidate;
        } else {
          lines.push(current);
          current = part;
        }
      }
      if (current) lines.push(current);

      // Hard cap on lines: merge any overflow into the last allowed line.
      const maxLines = this.config.labelMaxLines;
      if (lines.length > maxLines) {
        const merged = lines.slice(maxLines - 1).join(' / ');
        lines.length = maxLines - 1;
        lines.push(merged);
      }

      const allFit = lines.every((text, index) =>
        fits(text, this.lineRadius(baseRadius, lineGap, index, lines.length, flipped)));
      return { lines, fits: allFit };
    }

    /**
     * Finds one font size that lets every label fit and derives the outer padding
     * from the resulting maximum line count. Padding and radius depend on each other,
     * so iterate until the padding is stable (converges in 1–3 passes).
     * @returns {{fontSize:number, lineGap:number, padding:number, layouts:{lines:string[]}[]}}
     */
    computeLabelLayout() {
      const labels = this.config.labels || [];
      const wedgeDegrees = 360 / this.config.numberOfSectors;
      const sectorAngle = (2 * Math.PI) / this.config.numberOfSectors;
      const flippedFlags = labels.map((label, index) =>
        this.isFlipped(this.config.startDegrees + index * wedgeDegrees + wedgeDegrees / 2));
      const base = this.config.labelFontSize;
      const min = this.config.labelFontMin;
      let padding = this.config.padding;
      let result = null;

      this.context.save();

      for (let pass = 0; pass < 4; pass++) {
        const outerCircleRadius = this.canvasSize / 2 - padding;
        const baseRadius = outerCircleRadius + this.config.labelOffset;
        let chosen = null;

        for (let size = base; size >= min - 1e-9; size -= 0.5) {
          const lineGap = size * this.config.labelLineHeight;
          const layouts = labels.map((label, index) =>
            this.layoutLabel(label, size, baseRadius, lineGap, sectorAngle, flippedFlags[index]));
          chosen = { fontSize: size, lineGap, layouts };
          if (layouts.every(layout => layout.fits)) break;
        }

        const maxLines = chosen.layouts.reduce((max, layout) => Math.max(max, layout.lines.length), 1);
        const needed = this.config.labelOffset + (maxLines - 1) * chosen.lineGap + chosen.fontSize + 3;
        const nextPadding = Math.max(this.config.padding, Math.ceil(needed));

        result = { ...chosen, padding };

        if (nextPadding === padding) break;
        padding = nextPadding;
        result.padding = padding;
      }

      this.context.restore();
      return result;
    }

    drawArcLines(lines, fontSize, lineGap, baseRadius, centerDegrees) {
      const centerX = this.canvasSize / 2, centerY = this.canvasSize / 2;
      const angleRadians = this.toRadians(centerDegrees);
      // Labels in the lower half are flipped so they read left-to-right from outside.
      const shouldFlip = this.isFlipped(centerDegrees);
      const direction = shouldFlip ? -1 : +1;

      this.context.save();
      this.context.fillStyle = this.config.labelColor;
      this.context.font = this.labelFont(fontSize);
      this.context.textAlign = 'center';
      this.context.textBaseline = shouldFlip ? 'top' : 'bottom';

      lines.forEach((text, lineIndex) => {
        const lineRadius = this.lineRadius(baseRadius, lineGap, lineIndex, lines.length, shouldFlip);
        const characters = [...text];
        const characterWidths = characters.map(character => this.context.measureText(character).width);
        const totalPixels = characterWidths.reduce((sum, width) => sum + width, 0);
        const totalAngle = totalPixels / lineRadius;
        let currentAngle = angleRadians - direction * totalAngle / 2;

        this.context.save();
        this.context.translate(centerX, centerY);

        characters.forEach((character, characterIndex) => {
          const characterAngle = characterWidths[characterIndex] / lineRadius;
          const midAngle = currentAngle + direction * characterAngle / 2;

          this.context.save();
          this.context.translate(Math.cos(midAngle) * lineRadius, Math.sin(midAngle) * lineRadius);
          this.context.rotate(midAngle + (shouldFlip ? -Math.PI / 2 : Math.PI / 2));
          this.context.fillText(character, 0, 0);
          this.context.restore();

          currentAngle += direction * characterAngle;
        });

        this.context.restore();
      });

      this.context.restore();
    }

    render() {
      if (!this.canvas || !this.context || !this.config.labels || !this.config.data) {
        return;
      }

      const size = this.canvasSize;
      this.context.clearRect(0, 0, size, size);

      const labelLayout = this.computeLabelLayout();
      const padding = labelLayout.padding;

      const centerX = size / 2, centerY = size / 2;
      const wedgeDegrees = 360 / this.config.numberOfSectors;
      const innerRingRadius = this.config.innerPadding + this.config.ringWidth / 2;
      const middleRingRadius = innerRingRadius + this.config.ringWidth + this.config.ringGap;
      const outerRingRadius = middleRingRadius + this.config.ringWidth + this.config.ringGap;
      const maxRadius = size / 2 - padding;
      const scale = maxRadius / (outerRingRadius + this.config.ringWidth / 2);
      const scaledInnerRadius = innerRingRadius * scale;
      const scaledMiddleRadius = middleRingRadius * scale;
      const scaledOuterRadius = outerRingRadius * scale;
      const scaledRingWidth = this.config.ringWidth * scale;

      for (let sectorIndex = 0; sectorIndex < this.config.numberOfSectors; sectorIndex++) {
        const startAngle = this.config.startDegrees + sectorIndex * wedgeDegrees;
        const endAngle = startAngle + wedgeDegrees;
        const ringFlags = this.config.data[sectorIndex] || [0, 0, 0];

        if (ringFlags[0]) this.strokeArc(scaledInnerRadius, startAngle, endAngle, this.config.fillColor, scaledRingWidth);
        if (ringFlags[1]) this.strokeArc(scaledMiddleRadius, startAngle, endAngle, this.config.fillColor, scaledRingWidth);
        if (ringFlags[2]) this.strokeArc(scaledOuterRadius, startAngle, endAngle, this.config.fillColor, scaledRingWidth);
      }

      this.context.strokeStyle = this.config.gridColor;
      this.context.lineWidth = this.config.gridRadialWidth;

      const outerCircleRadius = scaledOuterRadius + scaledRingWidth / 2;

      for (let lineIndex = 0; lineIndex <= this.config.numberOfSectors; lineIndex++) {
        const angle = this.toRadians(this.config.startDegrees + lineIndex * wedgeDegrees);
        this.context.beginPath();
        this.context.moveTo(centerX, centerY);
        this.context.lineTo(centerX + Math.cos(angle) * outerCircleRadius, centerY + Math.sin(angle) * outerCircleRadius);
        this.context.stroke();
      }

      this.context.lineWidth = this.config.gridCircleWidth;
      [scaledInnerRadius - scaledRingWidth / 2, scaledMiddleRadius - scaledRingWidth / 2, scaledOuterRadius - scaledRingWidth / 2]
        .filter(radius => radius > 0.5)
        .forEach(radius => {
          this.context.beginPath();
          this.context.arc(centerX, centerY, radius, 0, Math.PI * 2);
          this.context.stroke();
        });

      this.context.beginPath();
      this.context.arc(centerX, centerY, outerCircleRadius, 0, Math.PI * 2);
      this.context.stroke();

      const labelBaseRadius = outerCircleRadius + this.config.labelOffset;

      for (let labelIndex = 0; labelIndex < this.config.numberOfSectors; labelIndex++) {
        const sectorCenterAngle = this.config.startDegrees + labelIndex * wedgeDegrees + wedgeDegrees / 2;
        const layout = labelLayout.layouts[labelIndex] || { lines: [''] };
        this.drawArcLines(layout.lines, labelLayout.fontSize, labelLayout.lineGap, labelBaseRadius, sectorCenterAngle);
      }
    }
  }

  customElements.define('flavor-wheel', FlavorWheel);
}
